import { Components } from '@mui/material/styles/components'
import { Theme } from '@mui/material/styles/createTheme'
import { Palette } from '@mui/material/styles/createPalette'
import {
  alertClasses,
  alpha,
  Box,
  iconButtonClasses,
  buttonClasses,
  inputBaseClasses,
  darken,
  chipClasses,
  formLabelClasses,
  outlinedInputClasses,
  menuItemClasses,
  toggleButtonGroupClasses,
  selectClasses,
  listItemIconClasses,
  popoverClasses,
  paperClasses,
} from '@mui/material'
import { Typography } from '@mui/material/styles/createTypography'
import dayjs from 'dayjs'
import { PickerComponents } from '@mui/x-date-pickers/themeAugmentation/components'
import { dayCalendarClasses, pickersDayClasses } from '@mui/x-date-pickers'
import { pickersArrowSwitcherClasses } from '@mui/x-date-pickers/internals'

import { Icon } from '../icon.tsx'

import { FontWeight } from './typography.ts'
import { breakpointsValues } from './breakpoints.ts'

export const createComponentsOverride = (
  isDark: boolean,
  palette: Palette,
  typography: Typography
): Components<Omit<Theme, 'components'>> & PickerComponents => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        minHeight: '100vh',
        scrollbarColor: `${palette.primary.main} ${palette.background.default}`,

        '&::-webkit-scrollbar': {
          backgroundColor: palette.background.default,
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: 8,
          backgroundColor: palette.background.default,
          minHeight: 24,
          border: '3px solid #2b2b2b',
        },
      },
    },
  },
  // https://www.figma.com/file/U4T9fkZqC0VQKJqGeLjIDi/%D0%91%D0%B8%D0%B1%D0%BB%D0%B8%D0%BE%D1%82%D0%B5%D0%BA%D0%B0-%D0%98%D0%9A?type=design&node-id=6371-10321&mode=design&t=DVqleeXiH2fEgMcI-4
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        info: (
          <Box sx={{ height: 28, borderRadius: 50, border: '2px solid', borderColor: palette.text.primary }}>
            <Icon name="Alert" size="initial" />
          </Box>
        ),
        error: (
          <Box sx={{ height: 28, borderRadius: 50, border: '2px solid', borderColor: palette.text.onControls }}>
            <Icon name="Alert" size="initial" />
          </Box>
        ),
      },
    },
    styleOverrides: {
      message: {
        padding: 0,
        overflow: 'initial',
      },
      icon: {
        padding: 0,
      },
      action: {
        paddingLeft: 24,
      },
      root: {
        borderRadius: 16,
        alignItems: 'center',
        position: 'relative',
        padding: '16px 24px',
        [`&.${alertClasses.colorError}.${alertClasses.filled}`]: {
          backgroundColor: palette.background.error,

          [`& .${alertClasses.message}`]: {
            ...typography.body1,
            fontWeight: 500,
            color: palette.text.onControls,
          },

          [`& .${alertClasses.icon}`]: {
            color: palette.text.onControls,
          },

          [`& .${iconButtonClasses.root}`]: {
            position: 'absolute',
            right: 8,
            top: 8,
            padding: 4,
            backgroundColor: palette.white.main,
            borderRadius: 20,
            color: palette.background.error,
          },
        },

        [`&.${alertClasses.colorInfo}.${alertClasses.filled}`]: {
          backgroundColor: palette.background.paper,
          [`& .${alertClasses.message}`]: {
            ...typography.body1,
            color: palette.text.primary,
          },

          [`& .${alertClasses.icon}`]: {
            color: palette.text.primary,
          },

          [`& .${iconButtonClasses.root}`]: {
            position: 'absolute',
            right: 8,
            top: 8,
            padding: 4,
            backgroundColor: palette.white.main,
            borderRadius: 20,
            color: palette.white.contrastText,
          },
        },
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      icon: <Icon name="RadioButton" colorScheme="text.secondary" size="md" />,
      checkedIcon: <Icon name="CheckedRB" colorScheme="text.secondary" size="md" />,
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        borderColor: palette.lines.grey,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: palette.lines.grey,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: 10,
        minWidth: 115,
        //maxWidth: 200,
        //whiteSpace: 'nowrap',
        borderColor: palette.divider,
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        fontFamily: 'GtAmericaLcg',
        fontWeight: FontWeight.Medium,
        //borderRadius: 4,
        padding: '0 4px',
        '&.Mui-selected': {
          color: palette.text.onControls,
        },
        '&.MuiPaginationItem-previousNext': {
          color: palette.text.primary,
          svg: {
            fill: 'currentColor',
          },
        },
      },
    },
  },
  MuiLink: {
    defaultProps: {
      variant: 'caption',
      underline: 'none',
    },
    styleOverrides: {
      root: {
        color: palette.text.link,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variant: 'body1',
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        color: palette.text.primary,
        paddingTop: 16,
        paddingBottom: 16,
      },
      colorPrimary: {
        backgroundColor: palette.background.default,
        backgroundImage: 'none',
        boxShadow: 'none',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: '8px 0 0',
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        [`& .${inputBaseClasses.root}`]: {
          borderRadius: 8,

          [`&.${inputBaseClasses.focused}`]: {
            fieldset: {
              borderWidth: 1,
            },
          },
          // стили для readonly, чтобы не подсвечивалось зеленым
          [`&.${inputBaseClasses.readOnly}`]: {
            [`&:hover, &:focus, &.${inputBaseClasses.focused}`]: {
              '& fieldset': {
                borderColor: isDark ? '#252F38' : '#F5F5F8',
              },
            },
          },
        },

        [`& .${inputBaseClasses.input}`]: {
          padding: '21px 20px 12px',
          '&:-webkit-autofill': {
            '-webkit-background-clip': 'text',
            '-webkit-box-shadow': '0 0 0 100px transparent inset !important',
            '-webkit-text-fill-color': palette.text.primary,
          },
        },

        [`& .${outlinedInputClasses.notchedOutline}`]: {
          top: 0,
          borderColor: isDark ? '#252F38' : '#F5F5F8',
          legend: {
            display: 'none',
          },
        },

        [`& .${formLabelClasses.root}`]: {
          ...typography.body2,
          transform: 'translate(20px, 16px) scale(1)',
          [`@media(max-width: ${breakpointsValues.lg}px)`]: {
            transform: 'translate(20px, 19px) scale(1)',
          },
          '&.Mui-focused, &.MuiFormLabel-filled': {
            transform: 'translate(20px, 8px) scale(0.75);',
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        [`& .${selectClasses.select} .${listItemIconClasses.root}`]: {
          display: 'none',
        },
        [`& .${popoverClasses.root}`]: {
          position: 'absolute',
          top: 56,
          left: 0,
          right: 'initial',
          bottom: 'initial',
          width: '100%',
        },
        [`& .${paperClasses.root}`]: {
          padding: '0!important',
          position: 'relative',
          maxWidth: '100%!important',
          top: '0!important',
          left: '0!important',
        },
      },
    },
    defaultProps: {
      size: 'medium',
      variant: 'outlined',
      MenuProps: {
        slotProps: {
          paper: {
            sx: {
              maxHeight: 220,
            },
          },
        },
        disablePortal: true,
        elevation: 2,
        anchorPosition: {
          top: 8,
          left: 0,
        },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      },
      IconComponent: (props) => (
        <Box
          {...props}
          sx={{
            width: '1em',
            height: '1em',
            fontSize: '1.5rem',
            right: '16px!important',
            display: 'inline-block',
            flexShrink: 0,
            userSelect: 'none',
          }}
        >
          <Icon name="ArrowDown" size="lg" />
        </Box>
      ),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        [`& .${toggleButtonGroupClasses.grouped}`]: {
          minWidth: 80,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      autoComplete: 'off',
    },
    styleOverrides: {
      root: {
        '& .clearButton': {
          color: palette.text.primary,
        },
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      label: {
        textTransform: 'capitalize',
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        textTransform: 'capitalize',
      },
    },
  },
  MuiDatePicker: {
    defaultProps: {
      slotProps: {
        field: { clearable: true },
        textField: { fullWidth: true },
      },
      slots: {
        clearIcon: () => <Icon name="Close" colorScheme="currentColor" />,
        openPickerIcon: () => <Icon name="Calendar" />,
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      root: {
        [`& .${pickersArrowSwitcherClasses.button}`]: {
          color: palette.text.primary,

          [`&.Mui-disabled`]: {
            color: alpha(palette.text.primary, 0.5),
          },
        },
      },
    },
  },
  MuiDateCalendar: {
    defaultProps: {
      slots: {
        leftArrowIcon: () => <Icon name="ArrowLeft" colorScheme="currentColor" />,
        rightArrowIcon: () => <Icon name="ArrowRight" colorScheme="currentColor" />,
        switchViewIcon: () => <Icon name="ArrowDown" />,
      },
      dayOfWeekFormatter: (date: dayjs.Dayjs) => date.format('dd').toUpperCase(),
    },
    styleOverrides: {
      root: {
        ...typography.body1,
        borderRadius: 8,
        backgroundColor: palette.background.default,

        [`& .${dayCalendarClasses.weekDayLabel}`]: {
          ...typography.caption,
          fontWeight: 500,
          color: palette.text.tertiary,
        },
        [`& .${pickersDayClasses.root}`]: {
          border: 'none',
          ...typography.body2,
          borderRadius: 4,
          fontWeight: 400,
          [`&.${pickersDayClasses.disabled}`]: {
            color: palette.text.tertiary,
          },
          [`&.${pickersDayClasses.today}`]: {
            border: 'none',
            color: palette.primary.main,
            background: 'transparent',
          },
          [`&.${pickersDayClasses.selected}`]: {
            color: palette.text.primary,
            backgroundColor: palette.primary.main,
          },
          '&:hover': {
            backgroundColor: palette.primary.light,
          },
        },
      },
    },
  },
  MuiSkeleton: {
    defaultProps: {
      animation: 'pulse',
      variant: 'rounded',
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontSize: 30,

        [`&.${iconButtonClasses.colorPrimary}`]: {
          backgroundColor: palette.primary.main,
          color: palette.text.onControls,

          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.04),
            color: palette.primary.main,
          },

          [`&.${iconButtonClasses.disabled}`]: {
            backgroundColor: alpha(palette.primary.main, 0.5),
            color: alpha(palette.text.onControls, 0.3),
            cursor: 'auto',
          },
        },
      },
      colorSecondary: {
        backgroundColor: palette.secondary.main,
        borderColor: palette.text.secondary,
        color: palette.text.primary,
        '&:hover': {
          backgroundColor: darken(palette.secondary.main, 0.05),
        },
      },
      sizeLarge: {
        padding: '16px',
      },
      sizeMedium: {
        padding: '12px',
      },
      sizeSmall: {
        padding: '8px',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        '& .MuiMenu-paper': {
          padding: 8,
          border: '1px solid',
          borderColor: palette.background.blue,
          boxShadow: 'none',
          minWidth: '100px!important',
          marginTop: 8,
          borderRadius: 8,
          backgroundColor: palette.background.default,
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        minHeight: 30,
        //borderRadius: 4,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',

        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: palette.background.paper,
          },
        },

        '&:hover': {
          backgroundColor: palette.background.paper,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      sizeSmall: {
        [`& .${chipClasses.label}`]: {
          padding: '2px 4px',
        },
      },
      sizeMedium: {
        [`& .${chipClasses.label}`]: {
          padding: '4px 8px',
        },
      },
      colorInfo: {
        backgroundColor: palette.chip.info.bg,
        color: palette.chip.info.text,
      },
      colorWarning: {
        backgroundColor: palette.chip.warning.bg,
        color: palette.chip.warning.text,
      },
      colorSuccess: {
        backgroundColor: palette.chip.success.bg,
        color: palette.chip.success.text,
      },
      colorError: {
        backgroundColor: palette.chip.error.bg,
        color: palette.chip.error.text,
      },
    },
  },
  MuiCircularProgress: {
    defaultProps: {
      size: 20,
    },
    styleOverrides: {
      colorPrimary: {
        color: palette.text.primary,
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      variant: 'contained',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        boxShadow: 'none',
        borderRadius: 8,

        '&:hover': {
          boxShadow: 'none',
        },

        [`&.${buttonClasses.contained}`]: {
          '&.MuiButton-colorWhite': {
            backgroundColor: palette.white.main,
            //color: '#212A34',
            color: palette.text.onControls,
          },
          [`&.${buttonClasses.colorPrimary}`]: {
            //backgroundColor: palette.primary.main,
            //color: '#212A34',
            color: palette.text.onControls,
          },
          [`&.${buttonClasses.colorSecondary}`]: {
            backgroundColor: palette.secondary.main,
            color: palette.text.primary,

            '&:hover': {
              backgroundColor: darken(palette.secondary.main, 0.15),
            },
          },
        },

        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.5,
          color: alpha(palette.text.primary, 0.4),
          borderColor: alpha(palette.text.primary, 0.4),

          [`&.${buttonClasses.contained}`]: {
            backgroundColor: alpha(palette.text.primary, 0.15),
            color: alpha(palette.text.primary, 0.5),
          },
        },
      },
      sizeLarge: {
        padding: '16px 32px',
        height: 56,
      },
      sizeMedium: {
        padding: '16px 24px',
        height: 48,
      },
      sizeSmall: {
        padding: '12px 16px',
        height: 40,
      },
    },
  },
})
